import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { employeeMiniProfile } from 'interfaces/EmployeeMiniProfile';
import { oneOneOneReactQueryKeys } from 'screens/oneOnOneRabbit/InstancePage/constants/oneOnOneReactQueryKeys';
import { OneOnOneActionItem } from 'screens/oneOnOneRabbit/interfaces/OneOnOneActionItem';
import { OneOnOneRequestI } from 'screens/oneOnOneRabbit/interfaces/common';
import { z } from 'zod';

import { createOneOnOneActionItem, getOneOnOneActionItems, updateOneOnOneActionItem } from '../services';

export const useOneOnOneActionItemsQuery = ({ masterId, instanceId }: OneOnOneRequestI) =>
  useQuery(
    [oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId],
    () => getOneOnOneActionItems({ masterId, instanceId })
    // {
    //   enabled: !!masterId
    // }
  );

const OneOnOneCreationI = z.object({
  actionItemText: z.string(),
  status: z.enum(['ACTIVE', 'COMPLETED']).nullable(),
  uiKey: z.string(),
  toIds: z.array(employeeMiniProfile),
  dueDate: z.string().nullable(),
  detailedStatus: z.enum(['DELAYED', 'ONGOING', 'COMPLETED', 'UNPLANNED', 'COMPLETED_ON_TIME']).nullable(),
});

export type OneOnOneActionItemCreateOrUpdateI = z.infer<typeof OneOnOneCreationI>;

export type OneOnOneActionItemType = z.infer<typeof OneOnOneActionItem>;

export const useCreateActionItemMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      updatedActionItem,
    }: {
      updatedActionItem: OneOnOneActionItemCreateOrUpdateI;
      onMutation: () => void;
    }) => await createOneOnOneActionItem({ masterId, instanceId, ...updatedActionItem }),
    onMutate: async ({
      updatedActionItem,
      onMutation,
    }: {
      updatedActionItem: OneOnOneActionItemCreateOrUpdateI;
      onMutation: () => void;
    }) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId]);

      const previousActionItems = queryClient.getQueryData([
        oneOneOneReactQueryKeys.oneOnOneActionItems,
        masterId,
        instanceId,
      ]);

      const transformedActionItem: OneOnOneActionItemType = {
        text: updatedActionItem.actionItemText,
        status: updatedActionItem.status,
        toIds: updatedActionItem.toIds,
        ...updatedActionItem,
      };
      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId],
        (old: OneOnOneActionItemType[]) => [...old, transformedActionItem]
      );

      onMutation();

      return { previousActionItems };
    },
    onError: (err: { message?: string }, previousActionItems) => {
      toast({
        title: err?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId],
        previousActionItems
      );
    },
    onSuccess: () => {
      toast({
        title: 'Initiative has been created',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId]);
    },
  });
};

export const useUpdateActionItemMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ updatedActionItem }: { updatedActionItem: OneOnOneActionItemType }) =>
      await updateOneOnOneActionItem({ masterId, instanceId, ...updatedActionItem }),
    onMutate: async ({ updatedActionItem }: { updatedActionItem: OneOnOneActionItemType }) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId]);

      const previousActionItems = queryClient.getQueryData([
        oneOneOneReactQueryKeys.oneOnOneActionItems,
        masterId,
        instanceId,
      ]);

      const transformedActionItem: OneOnOneActionItemType = {
        text: updatedActionItem.text,
        status: updatedActionItem.status,
        toIds: updatedActionItem.toIds,
        ...updatedActionItem,
      };
      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId],
        (old: OneOnOneActionItemType[]) =>
          old.map((actionItem) => {
            if (actionItem.instanceDataId === transformedActionItem.instanceDataId) {
              return transformedActionItem;
            }
            return actionItem;
          })
      );

      return { previousActionItems };
    },
    onError: (err: { message?: string }, previousActionItems) => {
      toast({
        title: err?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId],
        previousActionItems
      );
    },
    onSuccess: () => {
      toast({
        title: 'Initiative has been saved',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneActionItems, masterId, instanceId]);
    },
  });
};
